import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle,
  Clock,
  FileText,
  User,
  Calendar,
  Eye,
  Download,
  Send,
  AlertTriangle,
  CheckSquare,
  XCircle,
  RefreshCw,
  Plus,
  Search,
  Filter,
  Mail,
  Smartphone,
  Shield,
  Lock,
  FileCheck,
  Users,
  BarChart3,
  TrendingUp,
  Activity,
  Zap,
  Globe,
  Settings,
  Bell,
  Archive,
  Trash2,
  Edit,
  Copy,
  ExternalLink,
  Key,
  Fingerprint,
  Smartphone as Mobile,
  Monitor,
  Tablet,
  HelpCircle,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import DataTable from '../common/DataTable';
import ActionMenu from '../common/ActionMenu';
import ConfirmationModal from '../common/ConfirmationModal';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useHelp } from '../../context/HelpContext';
import PageHelp from '../help/PageHelp';
import ContractService from '../../services/contractService';
import ESignatureService from '../../services/eSignatureService';
import SourcingESignatureService from '../../services/sourcingESignatureService';
import AIService from '../../services/aiService';
import { useToast, ToastContainer } from '../common/Toast';
import API_CONFIG from '../../config/api';

const ESignature = ({ module: moduleProp = 'contract', onNewDocument }) => {
  const navigate = useNavigate();
  const { openPageHelp } = useHelp();
  const { success, error: showError, toasts, removeToast } = useToast();
  const isRfpModule = moduleProp === 'rfp';
  const basePath = isRfpModule ? '/sourcing/e-signature' : '/contracts/e-signature';
  const moduleTitle = isRfpModule ? 'RFP E-Signature' : 'E-Signature Management';
  const moduleDescription = isRfpModule
    ? 'Manage electronic signatures for RFP documents, track signing progress, and monitor vendor sign-offs'
    : 'Manage electronic signatures, track document status, and monitor signing progress';
  const apiService = isRfpModule ? SourcingESignatureService : ESignatureService;
  const helpPageId = isRfpModule ? 'rfp-e-signature' : 'e-signature';
  const [activeTab, setActiveTab] = useState('pending');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [signatureData, setSignatureData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [aiInsights, setAiInsights] = useState(null);

  // Transform source data to e-signature format
  const transformToSignature = (source) => {
    let signatureStatus = 'Pending Signature';
    if (source.status === 'EXECUTED' || source.status === 'APPROVED' || source.status === 'AWARDED') {
      signatureStatus = 'Completed';
    } else if (
      source.status === 'IN_REVIEW' ||
      source.status === 'PENDING_APPROVAL' ||
      source.status === 'EVALUATION'
    ) {
      signatureStatus = 'In Progress';
    } else if (
      source.status === 'EXPIRED' ||
      source.status === 'CANCELLED'
    ) {
      signatureStatus = 'Expired';
    }

    const completionPercentage =
      signatureStatus === 'Completed'
        ? 100
        : signatureStatus === 'In Progress'
          ? 50
          : 0;

    const endDate = source.endDate || source.responseDeadline;
    const isUrgent = endDate
      ? (new Date(endDate) - new Date()) / (1000 * 60 * 60 * 24) <= 7
      : false;

    const recipients = [
      {
        name: source.counterparty || source.scope?.substring(0, 50) || 'Unknown',
        email: source.counterpartyContact?.email || '',
        role: 'Signer',
        status: signatureStatus === 'Completed' ? 'Completed' : 'Pending',
      },
    ];

    const sourceId = source.contractId || source.rfpNumber || source.id;

    return {
      id: sourceId,
      documentName: source.title || source.name || 'Untitled Document',
      documentType: source.type || (isRfpModule ? 'RFP' : 'Contract'),
      contractId: isRfpModule ? undefined : sourceId,
      rfpId: isRfpModule ? sourceId : undefined,
      status: signatureStatus,
      priority: isUrgent ? 'High' : source.priority || 'Medium',
      sender: source.createdBy?.firstName || 'System',
      senderEmail: source.createdBy?.email || '',
      recipients: recipients,
      createdDate:
        source.createdAt || new Date().toISOString(),
      dueDate:
        endDate ||
        new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
      lastActivity:
        source.updatedAt || source.createdAt || new Date().toISOString(),
      completionPercentage: completionPercentage,
      totalSignatures: recipients.length,
      completedSignatures:
        signatureStatus === 'Completed' ? recipients.length : 0,
      documentSize: '2.4MB',
      fileFormat: 'PDF',
      signatureMethod: 'Digital Signature',
      authenticationMethod: 'Email + SMS',
      reminderSent: 0,
      maxReminders: 3,
      isUrgent: isUrgent,
      tags: source.tags || [],
      notes: source.description || source.notes || '',
    };
  };

  // Load signature data from backend
  const loadSignatureData = async () => {
    setLoading(true);
    try {
      let loadedData = [];

      try {
        const response = await apiService.getESignatureRequests();
        loadedData = response.data || [];
        setSignatureData(loadedData);
      } catch (eSigError) {
        console.warn(
          isRfpModule ? 'RFP e-signature' : 'E-signature' + ' API not available, falling back:',
          eSigError
        );
        if (!isRfpModule) {
          const response = await ContractService.getContracts({
            includeInactive: false,
          });
          const items = response.data || response || [];
          loadedData = items.map(transformToSignature);
        }
        setSignatureData(loadedData);
      }

      if (activeTab === 'analytics') {
        try {
          const aiResponse = await AIService.generateInsights(
            isRfpModule ? 'rfp-e-signatures' : 'e-signatures',
            '30d',
            {
              totalDocuments: loadedData.length,
              completed: loadedData.filter((d) => d.status === 'Completed')
                .length,
              pending: loadedData.filter(
                (d) => d.status === 'Pending Signature'
              ).length,
            }
          );
          setAiInsights(aiResponse);
        } catch (aiError) {
          console.error('Error generating AI insights:', aiError);
        }
      }
    } catch (error) {
      console.error('Error loading signature data:', error);
      showError('Failed to load signature data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSignatureData();
  }, [activeTab]);

  // Calculate statistics
  const stats = {
    totalDocuments: signatureData.length,
    pendingSignatures: signatureData.filter(
      (d) => d.status === 'Pending Signature'
    ).length,
    inProgress: signatureData.filter((d) => d.status === 'In Progress').length,
    completed: signatureData.filter((d) => d.status === 'Completed').length,
    expired: signatureData.filter((d) => d.status === 'Expired').length,
    urgentDocuments: signatureData.filter((d) => d.isUrgent).length,
    totalRecipients: signatureData.reduce(
      (sum, d) => sum + d.recipients.length,
      0
    ),
    averageCompletionTime: 2.5,
    completionRate:
      signatureData.length > 0
        ? (signatureData.filter((d) => d.status === 'Completed').length /
          signatureData.length) *
        100
        : 0,
  };

  // Filter documents by active tab
  const getFilteredDocuments = () => {
    let filtered = signatureData;

    switch (activeTab) {
      case 'pending':
        filtered = signatureData.filter(
          (d) => d.status === 'Pending Signature'
        );
        break;
      case 'in-progress':
        filtered = signatureData.filter((d) => d.status === 'In Progress');
        break;
      case 'completed':
        filtered = signatureData.filter((d) => d.status === 'Completed');
        break;
      case 'expired':
        filtered = signatureData.filter((d) => d.status === 'Expired');
        break;
      default:
        filtered = signatureData;
    }

    return filtered;
  };

  const handleRefresh = () => {
    loadSignatureData();
    success('Signature data refreshed');
  };

  const handleAddNew = () => {
    if (onNewDocument) {
      onNewDocument();
    } else {
      navigate(`${basePath}/edit/new`);
    }
  };

  // Header actions
  const headerActions = [
    {
      label: '',
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh signatures',
      disabled: loading,
    },
    {
      label: 'New Document',
      icon: <Plus size={18} />,
      onClick: handleAddNew,
      variant: 'primary',
    },
    {
      label: '',
      icon: <HelpCircle size={18} />,
      onClick: () => openPageHelp(helpPageId),
      variant: 'outline',
      tooltip: 'Help',
    },
  ];

  // KPI card configurations matching Dashboard
  const kpiCards = [
    {
      value: stats.totalDocuments,
      label: 'Total Documents',
      icon: FileText,
      iconColor: '#2D7DD2',
      iconBg: '#D6EFFF',
    },
    {
      value: stats.pendingSignatures,
      label: 'Pending Signatures',
      icon: Clock,
      iconColor: '#F59E0B',
      iconBg: '#FEF3C7',
    },
    {
      value: stats.inProgress,
      label: 'In Progress',
      icon: Activity,
      iconColor: '#4678be',
      iconBg: '#D6EFFF',
    },
    {
      value: stats.completed,
      label: 'Completed',
      icon: CheckCircle,
      iconColor: '#059669',
      iconBg: '#D1FAE5',
    },
    {
      value: stats.urgentDocuments,
      label: 'Urgent Documents',
      icon: AlertTriangle,
      iconColor: '#EF4444',
      iconBg: '#FEE2E2',
    },
    {
      value: `${Math.round(stats.completionRate)}%`,
      label: 'Completion Rate',
      icon: TrendingUp,
      iconColor: '#7C3AED',
      iconBg: '#EDE9FE',
    },
  ];

  // Define columns for DataTable
  const columns = [
    {
      key: 'id',
      label: 'Document ID',
      width: '100px',
    },
    {
      key: 'documentName',
      label: 'Document',
      width: '300px',
      render: (value, row) => (
        <div>
          <div className="fw-medium">{value}</div>
          <div className="text-muted" style={{ fontSize: '12px' }}>
            {row.documentType} • {row.contractId || row.rfpId || ''}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: '140px',
      render: (status) => {
        const statusConfig = {
          'Pending Signature': {
            className: 'badge-warning',
            icon: <Clock size={14} />,
          },
          'In Progress': {
            className: 'badge-primary',
            icon: <Activity size={14} />,
          },
          Completed: {
            className: 'badge-success',
            icon: <CheckCircle size={14} />,
          },
          Expired: { className: 'badge-danger', icon: <XCircle size={14} /> },
        };
        const config =
          statusConfig[status] || statusConfig['Pending Signature'];

        return (
          <span
            className={`badge ${config.className} d-flex align-items-center gap-1`}
          >
            {config.icon}
            {status}
          </span>
        );
      },
    },
    {
      key: 'priority',
      label: 'Priority',
      width: '100px',
      render: (priority) => {
        const priorityConfig = {
          High: { className: 'badge-danger' },
          Medium: { className: 'badge-warning' },
          Low: { className: 'badge-success' },
        };
        const config = priorityConfig[priority] || priorityConfig['Medium'];

        return <span className={`badge ${config.className}`}>{priority}</span>;
      },
    },
    {
      key: 'recipients',
      label: 'Recipients',
      width: '200px',
      render: (recipients) => (
        <div>
          <div
            style={{ fontSize: '14px', fontWeight: '500', color: '#1f2937' }}
          >
            {recipients.length} recipient{recipients.length !== 1 ? 's' : ''}
          </div>
          <div style={{ fontSize: '12px', color: '#6b7280' }}>
            {recipients.filter((r) => r.status === 'Completed').length} signed
          </div>
        </div>
      ),
    },
    {
      key: 'completionPercentage',
      label: 'Progress',
      width: '120px',
      render: (percentage) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div
            style={{
              width: '60px',
              height: '6px',
              backgroundColor: '#e5e7eb',
              borderRadius: '0',
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${percentage}%`,
                height: '100%',
                backgroundColor:
                  percentage === 100
                    ? '#10b981'
                    : percentage > 0
                      ? '#4678be'
                      : '#f59e0b',
                transition: 'width 0.3s ease',
              }}
            />
          </div>
          <span
            style={{ fontSize: '12px', fontWeight: '500', color: '#374151' }}
          >
            {percentage}%
          </span>
        </div>
      ),
    },
    {
      key: 'dueDate',
      label: 'Due Date',
      width: '120px',
      render: (date) => {
        const dueDate = new Date(date);
        const today = new Date();
        const isOverdue = dueDate < today;
        const isUrgent = (dueDate - today) / (1000 * 60 * 60 * 24) <= 1;

        return (
          <div
            style={{
              fontSize: '12px',
              color: isOverdue ? '#ef4444' : isUrgent ? '#f59e0b' : '#374151',
              fontWeight: isOverdue || isUrgent ? '500' : '400',
            }}
          >
            {dueDate.toLocaleDateString()}
            {(isOverdue || isUrgent) && (
              <div style={{ fontSize: '10px', marginTop: '2px' }}>
                {isOverdue ? 'Overdue' : 'Due Soon'}
              </div>
            )}
          </div>
        );
      },
    },
  ];

  // Handler functions
  const handleView = (document) => {
    const targetId = document.requestId || document.id || document.contractId || document.rfpId;

    if (!targetId) {
      console.error('No ID found for document:', document);
      showError('Error: Document ID not found');
      return;
    }
    navigate(`${basePath}/edit/${targetId}?mode=view`);
  };

  const handleEdit = (document) => {
    const targetId = document.requestId || document.id || document.contractId || document.rfpId;

    if (!targetId) {
      console.error('No ID found for document:', document);
      showError('Error: Document ID not found');
      return;
    }
    navigate(`${basePath}/edit/${targetId}`);
  };

  const handleSendReminder = async (document) => {
    try {
      success(`Reminder sent for: ${document.documentName}`);
    } catch (error) {
      console.error('Error sending reminder:', error);
      showError('Failed to send reminder');
    }
  };

  const handleDelete = (document) => {
    if (!document) return;
    setSelectedDocument(document);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      // Priority: requestId > id > contractId
      const targetId = selectedDocument?.requestId || selectedDocument?.id || selectedDocument?.contractId;

      if (targetId) {
        await apiService.deleteESignatureRequest(targetId);
        success('Document deleted successfully');
        await loadSignatureData();
      } else {
        console.error('No valid ID for deletion:', selectedDocument);
        showError('Error: Could not determine Document ID for deletion');
      }
      setShowDeleteModal(false);
      setSelectedDocument(null);
    } catch (error) {
      console.error('Error deleting document:', error);
      showError('Failed to delete document. ' + (error.response?.data?.message || error.message));
    } finally {
      setIsDeleting(false);
    }
  };

  // Download the original contract DOCX — reuses the same endpoint as /contracts/management.
  const handleDownloadOriginalDocx = (doc) => {
    const mongoId = doc.contractMongoId;
    if (!mongoId) {
      showError('No contract linked to this e-signature request.');
      return;
    }
    const base = API_CONFIG.API_BASE.replace(/\/+$/, '');
    const url = `${base}/contracts/${mongoId}/generate-docx`;
    // Use fetch with auth header to trigger download
    const token = localStorage.getItem('token');
    fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => {
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = `contract-${doc.contractId || mongoId}.docx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      })
      .catch((err) => {
        console.error('Failed to download original contract DOCX:', err);
        showError('Failed to download contract document. Please try again.');
      });
  };

  // Download the signed contract DOCX — only available after signing is Completed.
  const handleDownloadSignedDocx = (doc) => {
    if (!doc.signedDocumentUrl) {
      showError('Signed document is not yet available. Complete the signing process first.');
      return;
    }
    const a = document.createElement('a');
    a.href = doc.signedDocumentUrl;
    a.download = `signed-contract-${doc.contractId || doc.requestId}.docx`;
    a.target = '_blank';
    a.rel = 'noreferrer';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const tabs = [
    { id: 'pending', label: 'Pending Signatures', icon: Clock },
    { id: 'in-progress', label: 'In Progress', icon: Activity },
    { id: 'completed', label: 'Completed', icon: CheckCircle },
    { id: 'expired', label: 'Expired', icon: XCircle },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  ];

  // Chart colors
  const COLORS = [
    '#4678be',
    '#059669',
    '#F59E0B',
    '#EF4444',
    '#7C3AED',
    '#06B6D4',
  ];

  // Render Analytics Tab
  const renderAnalytics = () => {
    const statusData = [
      { name: 'Pending', value: stats.pendingSignatures },
      { name: 'In Progress', value: stats.inProgress },
      { name: 'Completed', value: stats.completed },
      { name: 'Expired', value: stats.expired },
    ];

    const priorityData = [
      {
        name: 'High',
        value: signatureData.filter((d) => d.priority === 'High').length,
      },
      {
        name: 'Medium',
        value: signatureData.filter((d) => d.priority === 'Medium').length,
      },
      {
        name: 'Low',
        value: signatureData.filter((d) => d.priority === 'Low').length,
      },
    ];

    // Calculate completion trends (last 6 months)
    const completionTrends = [];
    const now = new Date();
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleDateString('en-US', { month: 'short' });
      const monthCompleted = signatureData.filter((d) => {
        if (!d.lastActivity || d.status !== 'Completed') return false;
        const completedDate = new Date(d.lastActivity);
        return (
          completedDate.getMonth() === date.getMonth() &&
          completedDate.getFullYear() === date.getFullYear()
        );
      }).length;
      completionTrends.push({ month: monthName, completed: monthCompleted });
    }

    return (
      <div style={{ marginTop: '32px' }}>
        {/* AI Insights Card */}
        {aiInsights && (
          <div
            style={{
              backgroundColor: '#EDE9FE',
              border: '1px solid #7C3AED',
              borderRadius: '0',
              padding: '20px',
              marginBottom: '24px',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
                marginBottom: '12px',
              }}
            >
              <BarChart3 size={24} style={{ color: '#7C3AED' }} />
              <h5
                style={{
                  margin: 0,
                  color: '#7C3AED',
                  fontFamily: 'var(--font-family)',
                  fontSize: '16px',
                  fontWeight: '600',
                }}
              >
                AI-Powered Insights
              </h5>
            </div>
            <div
              style={{
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-family)',
                fontSize: '14px',
                lineHeight: '1.6',
              }}
            >
              {aiInsights.insights ||
                aiInsights.summary ||
                'Analyzing signature trends and patterns...'}
            </div>
          </div>
        )}

        {/* Charts Grid */}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px',
            marginBottom: '24px',
          }}
        >
          {/* Status Distribution */}
          <div
            style={{
              backgroundColor: 'white',
              border: '1px solid var(--border-color)',
              borderRadius: '16px',
              padding: '24px',
            }}
          >
            <h5
              style={{
                marginBottom: '20px',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-family)',
                fontSize: '18px',
                fontWeight: '600',
              }}
            >
              Documents by Status
            </h5>
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <RechartsPieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell
                        key={`cell-${index}`}
                        fill={COLORS[index % COLORS.length]}
                      />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                </RechartsPieChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Priority Distribution */}
          <div
            style={{
              backgroundColor: 'white',
              border: '1px solid var(--border-color)',
              borderRadius: '16px',
              padding: '24px',
            }}
          >
            <h5
              style={{
                marginBottom: '20px',
                color: 'var(--text-primary)',
                fontFamily: 'var(--font-family)',
                fontSize: '18px',
                fontWeight: '600',
              }}
            >
              Documents by Priority
            </h5>
            <div style={{ height: '300px' }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={priorityData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                  <XAxis
                    dataKey="name"
                    stroke="#6b7280"
                    style={{
                      fontSize: '12px',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                  <YAxis
                    stroke="#6b7280"
                    style={{
                      fontSize: '12px',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'white',
                      border: '1px solid #e5e7eb',
                      borderRadius: '0',
                      fontFamily: 'var(--font-family)',
                    }}
                  />
                  <Bar
                    dataKey="value"
                    fill="#4678be"
                    name="Documents"
                    radius={[0, 0, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Completion Trends */}
        <div
          style={{
            backgroundColor: 'white',
            border: '1px solid var(--border-color)',
            borderRadius: '16px',
            padding: '24px',
          }}
        >
          <h5
            style={{
              marginBottom: '20px',
              color: 'var(--text-primary)',
              fontFamily: 'var(--font-family)',
              fontSize: '18px',
              fontWeight: '600',
            }}
          >
            Completion Trends (Last 6 Months)
          </h5>
          <div style={{ height: '300px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={completionTrends}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis
                  dataKey="month"
                  stroke="#6b7280"
                  style={{ fontSize: '12px', fontFamily: 'var(--font-family)' }}
                />
                <YAxis
                  stroke="#6b7280"
                  style={{ fontSize: '12px', fontFamily: 'var(--font-family)' }}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #e5e7eb',
                    borderRadius: '0',
                    fontFamily: 'var(--font-family)',
                  }}
                />
                <Line
                  type="monotone"
                  dataKey="completed"
                  stroke="#4678be"
                  strokeWidth={2}
                  name="Completed"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title={moduleTitle}
        description={moduleDescription}
        actions={headerActions}
      />
      <div
        className="page-container"
        style={{
          width: '100%',
          maxWidth: '100%',
          paddingLeft: '24px',
          paddingRight: '24px',
        }}
      >
        {/* KPI Cards – StatCard reference styling (object-driven) */}
        <div className="grid-modern-4 w-full gap-6 mb-8">
          {kpiCards.map((card, index) => {
            const IconComponent = card.icon;

            return (
              <div
                key={index}
                className="relative border border-[var(--border-color)] bg-white"
              >
                {/* Accent line */}
                <div className="h-1 w-full bg-[var(--primary-color)]" />

                {/* Content */}
                <div className="relative flex items-center gap-3 p-4">
                  {/* Icon */}
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center"
                    style={{ background: card.iconBg }}
                  >
                    <IconComponent
                      size={20}
                      style={{ color: card.iconColor }}
                    />
                  </div>

                  {/* Text */}
                  <div>
                    <div className="text-[20px] font-bold text-[var(--text-primary)]">
                      {card.value}
                    </div>
                    <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                      {card.label}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabbed Navigation */}
        <div className="mb-8 border border-slate-200">
          <div className="w-full flex items-center bg-white border-b-1 border-slate-200">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`
                    flex items-center justify-center gap-2 px-5 py-3 w-full
                    text-sm font-medium
                    border-b-2 transition-colors duration-200
                    ${isActive
                      ? '!border-b-2 border-[var(--primary-color)] text-[var(--primary-color)] bg-white'
                      : 'border-transparent text-[var(--text-secondary)] hover:bg-slate-100 text-slate-500'
                    }
                  `}
                  style={{ fontFamily: 'var(--font-family)' }}
                >
                  <Icon size={18} />
                  <span>{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content - DataTable */}
        {(activeTab === 'pending' ||
          activeTab === 'in-progress' ||
          activeTab === 'completed' ||
          activeTab === 'expired') && (
            <div className="page-tab-content" style={{ marginTop: '32px' }}>
              {loading ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                    flexDirection: 'column',
                    gap: '16px',
                    backgroundColor: 'white',
                    border: '1px solid var(--border-color)',
                    padding: '40px',
                  }}
                >
                  <div className="loading-spinner"></div>
                  <p
                    style={{
                      color: 'var(--text-secondary)',
                      fontFamily: 'var(--font-family)',
                    }}
                  >
                    Loading signature documents...
                  </p>
                </div>
              ) : (
                <div
                  className="page-table-container"
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid var(--border-color)',
                  }}
                >
                  <DataTable
                    data={getFilteredDocuments()}
                    columns={columns}
                    title="E-Signature Documents"
                    searchable={true}
                    sortable={true}
                    exportable={true}
                    pagination={true}
                    pageSize={10}
                    showActions={true}
                    showCheckboxes={false}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    customActions={[
                      {
                        type: 'download-original-docx',
                        label: 'Download Original Contract',
                        customIcon: Download,
                        onClick: handleDownloadOriginalDocx,
                      },
                      {
                        type: 'download-signed-docx',
                        label: 'Download Signed Contract',
                        customIcon: Download,
                        disabled: (row) => !row?.signedDocumentUrl || row?.status !== 'Completed',
                        onClick: handleDownloadSignedDocx,
                      },
                    ]}
                    searchPlaceholder="Search documents by name, ID, or sender..."
                    emptyMessage="No signature documents found. Click 'New Document' to get started."
                    initialHiddenColumns={['id']}
                  />
                </div>
              )}
            </div>
          )}

        {/* Analytics Tab */}
        {activeTab === 'analytics' && renderAnalytics()}

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete E-Signature Document"
          message={`Are you sure you want to delete document "${selectedDocument?.documentName}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          isLoading={isDeleting}
        />

        <PageHelp />
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default ESignature;